import React from 'react';
import { useNavigate } from 'react-router-dom';
import useModuleProgress from '../hooks/useModuleProgress';
import '../styles/pages/_module-pages.scss';
import '../styles/pages/_learn-assessments.scss';
import '../styles/pages/_quiz-completion.scss';

interface ModuleSection {
  title: string;
  content: string[];
  illustrationText?: string;
  additionalSection?: {
    title: string;
    content: string[];
  };
  additionalSection2?: {
    title: string;
    content: string[];
  };
}

const moduleContent: ModuleSection[] = [
  {
    title: 'Emotional Awareness and Regulation',
    content: [
      '<strong>Overview:</strong> Based on the emotional components of DASS-21, this module helps you identify your emotional states and regulate them healthily.',
      '<br/>',
      '<strong>Key Topics:</strong>',
      '• Understanding common emotions: stress, anxiety, sadness',
      '• Recognizing signs of emotional imbalance',
      '• Techniques for emotional regulation',
      '<br/>',
      '<strong>Discussion:</strong> Your emotions are messages, not enemies. Learning to read them helps you respond rather than react.'
    ]
  },
  {
    title: 'Understanding Common Emotions',
    content: [
      'Emotions are natural responses that provide valuable information about your needs and values. Here are some common emotions and their purposes:',
      '<br/>',
      '<strong>Stress:</strong> Your body\'s alarm system signaling that you need to prepare for action. It\'s helpful in short bursts but harmful when chronic.',
      '<br/>',
      '<strong>Anxiety:</strong> A future-oriented emotion that alerts you to potential threats. It becomes problematic when it prevents you from living fully in the present.',
      '<br/>',
      '<strong>Sadness:</strong> A signal that something important to you has been lost or threatened. It helps you process grief and motivates you to seek connection.',
      '<br/>',
      '<strong>Anger:</strong> A protective emotion that energizes you to defend boundaries or address injustice. It becomes destructive when it leads to aggression.',
      '<br/>',
      '<strong>Joy:</strong> A reward system emotion that reinforces positive experiences and social bonds.',
      '<br/>',
      '<strong>Fear:</strong> A survival mechanism that keeps you safe from danger, but can become paralyzing when triggered inappropriately.'
    ]
  },
  {
    title: 'Recognizing Signs of Emotional Imbalance',
    content: [
      'Emotional imbalance occurs when emotions become overwhelming, persistent, or interfere with daily functioning. Here are common signs:',
      '<br/>',
      '<strong>Physical Signs:</strong>',
      '• Chronic fatigue or insomnia',
      '• Headaches, muscle tension, or stomach issues',
      '• Changes in appetite or sleep patterns',
      '• Increased heart rate or shallow breathing',
      '<br/>',
      '<strong>Behavioral Signs:</strong>',
      '• Withdrawing from social activities',
      '• Increased irritability or mood swings',
      '• Difficulty concentrating or making decisions',
      '• Using substances to cope with emotions',
      '<br/>',
      '<strong>Cognitive Signs:</strong>',
      '• Racing thoughts or constant worry',
      '• Negative self-talk or distorted thinking',
      '• Difficulty seeing positive aspects of situations',
      '• Feeling overwhelmed by small challenges'
    ]
  },
  {
    title: 'Techniques for Emotional Regulation',
    content: [
      'Emotional regulation involves recognizing emotions and choosing how to respond. Here are effective techniques:',
      '<br/>',
      '<strong>The 3-Step Pause:</strong>',
      '• <strong>Stop:</strong> Pause before reacting',
      '• <strong>Name:</strong> Identify the emotion ("I feel angry")',
      '• <strong>Choose:</strong> Select a thoughtful response',
      '<br/>',
      '<strong>Emotional Labeling:</strong> Use the formula "I feel ___ because ___." This creates distance from the emotion and helps you understand it.',
      '<br/>',
      '<strong>Grounding Exercises:</strong>',
      '• <strong>5-4-3-2-1 Technique:</strong> Name 5 things you see, 4 you can touch, 3 you hear, 2 you smell, 1 you taste',
      '• <strong>Breathing:</strong> Try 4-7-8 breathing (inhale 4 counts, hold 7, exhale 8)',
      '• <strong>Physical Movement:</strong> Stretch, walk, or use progressive muscle relaxation'
    ],
    additionalSection: {
      title: 'Mini-Exercise',
      content: [
        'List three emotions you felt most often this week. For each one:',
        '• What triggered the emotion?',
        '• How did you respond?',
        '• What would be a more balanced response?',
        '<br/>',
        '<strong>Example:</strong>',
        '• <strong>Emotion:</strong> Frustration',
        '• <strong>Trigger:</strong> Traffic jam made me late for work',
        '• <strong>Response:</strong> Yelled at other drivers, arrived stressed',
        '• <strong>Better Response:</strong> Used breathing techniques, called ahead to explain delay',
        '<br/>',
        '<strong>Takeaway:</strong> Regulating emotions doesn\'t mean suppressing them–it means understanding and managing them effectively. Emotions are data, not dictators.'
      ]
    }
  },
  {
    title: 'Ready to Test Your Knowledge?',
    content: [
      'You\'ve completed Module 2: Emotional Awareness and Regulation! Now, let\'s reinforce your learning with a quick quiz.'
    ],
    additionalSection: {
      title: 'Next Steps',
      content: [
        'Click "Take the Quiz" below to test your knowledge about emotional awareness and regulation techniques.',
        'The quiz will help you review key concepts about understanding emotions and managing them effectively.'
      ]
    }
  }
];

const EmotioPsychoRationalModule2Page: React.FC = () => {
  const navigate = useNavigate();
  const { currentStep, setCurrentStep, isLoading, saveProgress, isCompleted } = useModuleProgress('emotioPsychoRationalM2');
  const totalSteps = moduleContent.length;

  const safeCurrentStep = Math.max(0, Math.min(currentStep, totalSteps - 1));
  const currentModule = moduleContent[safeCurrentStep];
  const progress = ((safeCurrentStep + 1) / totalSteps) * 100;

  const handleNext = async () => {
    if (currentStep < totalSteps - 1) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      await saveProgress(nextStep, false);
    } else {
      await saveProgress(currentStep, true);
      navigate('/panel/learn/emotio-psycho-rational-module-2/quiz');
    }
  };

  const handleBack = async () => {
    if (currentStep === 0) {
      navigate('/panel/learn/emotio-psycho-rational-resources');
      return;
    }
    const prevStep = currentStep - 1;
    setCurrentStep(prevStep);
    await saveProgress(prevStep, isCompleted);
  };

  const showCompletionMessage = currentStep === totalSteps - 1;

  if (isLoading) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Loading your progress...</p>
      </div>
    );
  }

  if (!currentModule) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Error loading module content. Please try refreshing the page.</p>
      </div>
    );
  }

  return (
    <div className="module-form">
      <div className="module-container">
        <div 
          className="module-left emotio-psycho-rational-panel"
        >
          <div className="module-header">
            <h1>Module 2</h1>
            <p className="module-subtitle">Emotional Awareness and Regulation</p>
            <hr className="module-divider" />
            <p className="module-tool-info">
              Learn to identify your emotional states and regulate them healthily. Discover techniques for emotional awareness and management based on DASS-21 principles.
            </p>
          </div>
        </div>

        <div className="module-right">
          <div className="module-content"> 
            <div className="module-progress">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }}></div>
              </div>
              <span className="progress-text">
                Page {currentStep + 1} of {totalSteps}
              </span>
            </div>

            <div className="module-section">
              <h2 className="module-title"> 
                {currentModule.title}
              </h2>
              <div className="content-section">
                {currentModule.content.map((paragraph, index) => (
                  <p key={index} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                ))}
              </div>
            </div>

            {currentModule.additionalSection && (
              <div className="module-section">
                <h2 className="module-title">{currentModule.additionalSection.title}</h2>
                <div className="content-section">
                  {currentModule.additionalSection.content.map((paragraph: string, index: number) => (
                    <p key={`additional-${index}`} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                  ))}
                </div>
              </div>
            )}
            {currentModule.additionalSection2 && (
              <div className="module-section">
                <h2 className="module-title">{currentModule.additionalSection2.title}</h2>
                <div className="content-section">
                  {currentModule.additionalSection2.content.map((paragraph: string, index: number) => (
                    <p key={`additional2-${index}`} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="navigation-buttons">
            <button
              className="btn-secondary"
              onClick={handleBack}
              disabled={false} 
            >
              {currentStep === 0 ? 'Exit Module' : 'Previous'}
            </button>
            
            {showCompletionMessage ? (
              <button
                className="btn-primary"
                onClick={() => navigate('/panel/learn/emotio-psycho-rational-module-2/quiz')}
              >
                Take the Quiz
              </button>
            ) : (
              <button
                className="btn-primary"
                onClick={handleNext}
              >
                Next
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmotioPsychoRationalModule2Page;


